import React, { useState, useEffect, useRef } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import { connect } from 'react-redux';

import * as Bixby from '$/core';
import AnalystNotes from '$/components/analystNotes';
import BixCheckbox from '$/components/global/bixCheckbox';
import BixReadonlySelect from '$/components/global/bixReadonlySelect';
import BixbyQuillWrapper from '$/components/quill';
import InputBlock from '$/components/global/inputBlock';
import { AppState } from '$/store';
import { updateDraft } from '$/store/draft/actions';
import { KpiState } from '$/core/enums';

export interface ICompanyAnalystProps {
  currentCompany?: Bixby.Models.AdminCompany;
  draftCompany?: Bixby.Models.DraftCompany;
  isEditing?: boolean;
  discardDraft?: boolean;

  companyReportingTypes?: Bixby.Models.IIdentifiable[];

  dispatchUpdateDraftCompany: (companyId: string, updatedCompany: Bixby.Models.JsonPatchOperation[]) => void;
}

export interface CompanyAnalystValues {
  reportingType_Id: string;
  reportingDetails: string;
  capitalStructureProForma: boolean;
  is6K20FReporter: boolean;
  hasKnownRoguePost: boolean;
  hasKnownRoguePostShadow: boolean;
  doesNotHaveFinancials: boolean;
  kpiState: KpiState;
}

const CompanyAnalyst: React.FC<ICompanyAnalystProps> = props => {
  const { currentCompany, draftCompany, isEditing, discardDraft, companyReportingTypes, dispatchUpdateDraftCompany } =
    props;
  const [initialFormValues, setInitialFormValues] = useState({} as CompanyAnalystValues);

  const formikRef = useRef<FormikProps<CompanyAnalystValues>>(null);

  const kpiStates: Bixby.Models.IIdentifiable[] = [
    {
      id: Bixby.Enums.KpiState.KpisNotYetReviewed,
      name: 'KPIs Not Yet Reviewed',
    },
    {
      id: Bixby.Enums.KpiState.KpisNotVisible,
      name: 'KPIs Not Visible',
    },
    {
      id: Bixby.Enums.KpiState.KpisVisible,
      name: 'KPIs Visible',
    },
  ];

  useEffect(() => {
    if (!isEditing && currentCompany && !Bixby.Utils.isNullOrEmpty(currentCompany)) {
      setInitialFormValues({
        reportingType_Id: currentCompany.reportingType_Id,
        reportingDetails: currentCompany.reportingDetails || '',
        capitalStructureProForma: currentCompany.capitalStructureProForma,
        is6K20FReporter: currentCompany.is6K20FReporter,
        hasKnownRoguePost: currentCompany.hasKnownRoguePost,
        hasKnownRoguePostShadow: currentCompany.hasKnownRoguePostShadow,
        doesNotHaveFinancials: currentCompany.doesNotHaveFinancials,
        kpiState: currentCompany.kpiState,
      });
    } else if (isEditing && !Bixby.Utils.isNullOrEmpty(draftCompany) && draftCompany) {
      setInitialFormValues({
        reportingType_Id: draftCompany.reportingType_Id,
        reportingDetails: draftCompany.reportingDetails || '',
        capitalStructureProForma: draftCompany.capitalStructureProForma,
        is6K20FReporter: draftCompany.is6K20FReporter,
        hasKnownRoguePost: draftCompany.hasKnownRoguePost,
        hasKnownRoguePostShadow: draftCompany.hasKnownRoguePostShadow,
        doesNotHaveFinancials: draftCompany.doesNotHaveFinancials,
        kpiState: draftCompany.kpiState,
      });
    }
  }, [isEditing, currentCompany, draftCompany]);

  const parser = new DOMParser();
  const setReportingDetails = (form: any, content: string) => {
    let { textContent } = parser.parseFromString(content, 'text/html').documentElement;
    if (textContent && Bixby.Utils.isDefined(textContent) && textContent?.trim()?.length <= 0) {
      textContent = '';
    } else {
      textContent = content;
    }
    form.setFieldValue('reportingDetails', textContent);
  };

  useEffect(() => {
    if (discardDraft) {
      handleResetForm();
    }
  }, [discardDraft]);

  const handleResetForm = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  return currentCompany && !Bixby.Utils.isNullOrEmpty(initialFormValues) ? (
    <div className="admin-profile-section bottom">
      <div className="profile-input-container">
        <div className="profile-input-column">
          <Formik
            innerRef={formikRef}
            initialValues={initialFormValues}
            enableReinitialize
            onSubmit={(values: CompanyAnalystValues) => {
              const commandArray = Object.entries(values).map(field => {
                return {
                  op: 'replace',
                  path: `/${field[0]}`,
                  value: field[1],
                  from: '',
                } as Bixby.Models.JsonPatchOperation;
              });
              if (draftCompany) {
                dispatchUpdateDraftCompany(draftCompany.id, commandArray);
              }
            }}
            render={({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <h3>Internal use for Bixby earnings</h3>

                <div className="profile-input-container">
                  <div className="profile-input-column">
                    <BixReadonlySelect
                      title="Reporting Type"
                      fieldName="reportingType_Id"
                      fieldId={initialFormValues.reportingType_Id}
                      showDefaultOption={true}
                      isEditing={isEditing}
                      options={companyReportingTypes}
                    />
                  </div>
                  <div className="profile-input-column">
                    <Field
                      name="capitalStructureProForma"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id="capitalStructureProForma"
                          checked={field.value}
                          disabled={!isEditing}
                          onChange={field.onChange(field.name)}>
                          Capital Structure: Pro Forma
                        </BixCheckbox>
                      )}
                    />
                    <BixReadonlySelect
                      title="KPI State"
                      fieldName="kpiState"
                      fieldId={initialFormValues.kpiState}
                      showDefaultOption={true}
                      isEditing={isEditing}
                      options={kpiStates}
                      sort={false}
                    />
                    {currentCompany?.isSECPublic && (
                      <Field
                        name="is6K20FReporter"
                        render={({ field }: { [key: string]: any }) => (
                          <BixCheckbox
                            id="is6K20FReporter"
                            checked={field.value}
                            disabled={!isEditing}
                            title="Indicates whether or not the name is a 6K/20F reporter."
                            onChange={field.onChange(field.name)}>
                            6K / 20F reporter
                          </BixCheckbox>
                        )}
                      />
                    )}
                    <Field
                      name="hasKnownRoguePost"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id="hasKnownRoguePost"
                          checked={field.value}
                          disabled={!isEditing}
                          onChange={field.onChange(field.name)}>
                          Known RP
                        </BixCheckbox>
                      )}
                    />
                    <Field
                      name="hasKnownRoguePostShadow"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id="hasKnownRoguePostShadow"
                          checked={field.value}
                          disabled={!isEditing}
                          onChange={field.onChange(field.name)}>
                          Known RP Shadow
                        </BixCheckbox>
                      )}
                    />
                    <Field
                      name="doesNotHaveFinancials"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id="doesNotHaveFinancials"
                          checked={field.value}
                          disabled={!isEditing}
                          onChange={field.onChange(field.name)}>
                          Does Not Have Financials
                        </BixCheckbox>
                      )}
                    />
                  </div>
                </div>

                <InputBlock title="Reporting Details">
                  <Field
                    name="reportingDetails"
                    disabled={!isEditing}
                    render={({ field, form }: { [key: string]: any }) => {
                      return (
                        <BixbyQuillWrapper
                          value={field.value}
                          onBlur={() => setReportingDetails(form, field.value)}
                          onChange={content => setReportingDetails(form, content)}
                          readOnly={!isEditing}
                        />
                      );
                    }}
                  />
                </InputBlock>
                {isEditing && (
                  <button className="bix_button" type="submit">
                    Save as Draft
                  </button>
                )}
              </form>
            )}></Formik>
        </div>
        <div className="profile-input-column">
          <h3>Internal Notes</h3>
          <AnalystNotes currentCompany={currentCompany} />
        </div>
      </div>
    </div>
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.draftState.isLoading,
    error: state.draftState.error,
    draftCompany: state.draftState.draftCompany,
    isEditing: state.draftState.isEditing,
    discardDraft: state.draftState.discardDraft,
    currentCompany: state.companyState.currentCompany,

    companyReportingTypes: state.companyReportingTypeState.data,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateDraftCompany: (companyId: string, draftCompanyCommand: Bixby.Models.JsonPatchOperation[]) =>
    dispatch(updateDraft(companyId, draftCompanyCommand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyAnalyst);
